import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import i18next from "src/i18n/config";
import { useAppSettingsService } from "src/services/appSettingsService/appSettingsService";
import { useDispatch, useSelector } from "src/store";

import { updateProvisioning } from "@dashboard/provisioning/store/index";
import { selectProvisionings } from "@dashboard/provisioning/store/selectors";
import { PageContainer } from "@dashboard/shared/components/PageContainer";
import {
    useCurrentSubscriptionExpired,
    useCurrentWorkspace,
    useIsWorkspaceFeatureLimitReached,
} from "@dashboard/workspaces/api/helpers";
import { Box, DialogContentText, TableBody, TableRow, Typography } from "@mui/material";
import { withMetaDescription } from "@navigation/utils/withMetaDescription";
import { SecondaryButton } from "@shared/CustomButton";
import { ExpiredWrapper } from "@shared/ExpiredWrapper";
import { LoadingBox } from "@shared/Loading";
import {
    MinimalTable,
    MinimalTableCell,
    MinimalTableContainer,
    MinimalTableHeader,
    MinimalTableHeaderCell,
} from "@shared/table/MinimalTableComponents";
import { useContainerOverflow } from "@shared/table/useContainerOverflow";
import { showErrorToast, showSuccessToast } from "@shared/toasts/Toasts";

import { ConfirmDialog } from "../devices/components/shared";
import { Provisioning } from "../provisioning/types";

import { useGetCurrentWorkspaceFdroids } from "./api/helpers";
import { CreateFdroidRepoDialog } from "./CreateFdroidRepoDialog/CreateFdroidRepoDialog";
import { useDeleteFdroidMutation } from "./api";
import { useStyles } from "./RepositoriesPage.style";
import RepositoryTableItem from "./RepositoryTableItem";

export type DeleteProps = {
    title: string;
    subscriptionId: number;
    id: number;
};

const RepositoriesPage = () => {
    const [selectedRepoForDeletion, setSelectedRepoForDeletion] = useState<DeleteProps | null>(null);
    const [createDialogOpen, setCreateDialogOpen] = useState(false);

    const { classes, cx } = useStyles();
    const dispatch = useDispatch();
    const { t } = useTranslation(["pages", "modals", "common"]);

    const { data: fdroidRepos = [] } = useGetCurrentWorkspaceFdroids();
    const overLimit = useIsWorkspaceFeatureLimitReached("FDroidRepoLimit");
    const isExpired = useCurrentSubscriptionExpired();
    const provisioningSettings = useSelector(selectProvisionings);
    const { isLoading } = useCurrentWorkspace();
    const { effectiveWorkspaceSettings } = useAppSettingsService();
    const provisioningEnabled = effectiveWorkspaceSettings.isProvisioningPageEnabled;
    const [deleteFdroid] = useDeleteFdroidMutation();

    const { ref: tableContainerRef, contentOverflows } = useContainerOverflow(true, [fdroidRepos.length, provisioningEnabled]);

    const getCreateButtonTooltip = () => {
        if (overLimit) {
            return t("pages:repositoryPage.tooltip.createNewRepository");
        }
        if (isExpired) {
            return t("pages:repositoryPage.tooltip.subscriptionExpired");
        }
        return "";
    };

    const handleDelete = () => {
        if (selectedRepoForDeletion) {
            const payload = {
                subscriptionId: selectedRepoForDeletion.subscriptionId,
                repoId: selectedRepoForDeletion.id,
            };

            const updatedProvisionings: Provisioning[] = [];

            provisioningSettings.forEach((item) => {
                if (item.settings?.fDroidRepos.some((repo) => repo.id === payload.repoId)) {
                    const updatedFDroidRepos = item.settings.fDroidRepos.filter((repo) => repo.id !== payload.repoId);

                    const updatedItem = {
                        ...item,
                        settings: {
                            ...item.settings,
                            fDroidRepos: updatedFDroidRepos,
                        },
                    };

                    updatedProvisionings.push(updatedItem);
                }
            });

            Promise.all(updatedProvisionings.map((provisioning) => dispatch(updateProvisioning(provisioning)).unwrap()))
                .then(() => deleteFdroid(payload).unwrap())
                .then(() => showSuccessToast(t("pages:repositoryPage.toast.deleteRepositorySuccess")))
                .catch(({ message = t("pages:repositoryPage.toast.deleteRepositoryError") }) => showErrorToast(message))
                .finally(() => setSelectedRepoForDeletion(null));
        }
    };

    if (isLoading) {
        return <LoadingBox />;
    }

    return (
        <PageContainer fitViewport>
            <ExpiredWrapper>
                <Box className={classes.tableLayout}>
                    <Box className={classes.toolbar}>
                        <SecondaryButton
                            onClick={() => setCreateDialogOpen(true)}
                            disabled={overLimit || isExpired}
                            tooltipProps={{ title: getCreateButtonTooltip() }}
                            startIcon={<i className="fa-solid fa-plus" />}
                        >
                            <Typography>{t("pages:repositoryPage.button.createNewRepository")}</Typography>
                        </SecondaryButton>
                    </Box>
                    <MinimalTableContainer
                        ref={tableContainerRef}
                        className={cx(classes.tableContainer, !contentOverflows && classes.tableContainerNoScroll)}
                    >
                        <MinimalTable>
                            <MinimalTableHeader>
                                <TableRow>
                                    <MinimalTableHeaderCell>{t("common:label.url")}</MinimalTableHeaderCell>
                                    <MinimalTableHeaderCell>{t("common:label.title")}</MinimalTableHeaderCell>
                                    <MinimalTableHeaderCell>{t("common:label.description")}</MinimalTableHeaderCell>
                                    {provisioningEnabled ? (
                                        <MinimalTableHeaderCell align="center">
                                            {t("pages:repositoryPage.table.header.usedInProvisioning")}
                                        </MinimalTableHeaderCell>
                                    ) : null}
                                    <MinimalTableHeaderCell></MinimalTableHeaderCell>
                                </TableRow>
                            </MinimalTableHeader>

                            <TableBody>
                                {fdroidRepos.length ? (
                                    fdroidRepos.map((repo, index) => (
                                        <RepositoryTableItem
                                            key={index}
                                            index={index}
                                            repo={repo}
                                            provisioningEnabled={provisioningEnabled}
                                            handleOpenDeleteDialog={() => setSelectedRepoForDeletion(repo)}
                                        />
                                    ))
                                ) : (
                                    <TableRow>
                                        <MinimalTableCell align="center" colSpan={6}>
                                            <Typography>{t("pages:repositoryPage.table.noRepositories")}</Typography>
                                        </MinimalTableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </MinimalTable>
                    </MinimalTableContainer>
                    <CreateFdroidRepoDialog isOpen={createDialogOpen} onClose={() => setCreateDialogOpen(false)} />
                    {selectedRepoForDeletion && (
                        <ConfirmDialog
                            title={t("modals:deleteRepository.title", { title: selectedRepoForDeletion.title })}
                            dangerButton
                            primaryActionText={t("common:button.delete")}
                            open={!!selectedRepoForDeletion}
                            onConfirm={handleDelete}
                            onClose={() => setSelectedRepoForDeletion(null)}
                        >
                            <DialogContentText>
                                <Trans
                                    ns="modals"
                                    i18nKey="deleteRepository.description"
                                    values={{ title: selectedRepoForDeletion.title }}
                                    components={{ bold: <span style={{ fontWeight: "bold" }} /> }}
                                />
                            </DialogContentText>
                        </ConfirmDialog>
                    )}
                </Box>
            </ExpiredWrapper>
        </PageContainer>
    );
};

export default withMetaDescription(RepositoriesPage, {
    title: i18next.t("pages:repositoryPage.metaTitle"),
    description: i18next.t("pages:repositoryPage.metaDescription"),
});
